import { UserRole } from "@fitness-app/data-models";
import { type ChatChannel, type ChatChannelWithLastMessage } from "@fitness-app/data-models/entities/Chat";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";
import { subscribeToLatestChannelFailed, subscribeToLatestChannelSuccess, updateChannel } from "../reducer";
import { unsubscribeFromLatestChannel } from "./unsubscribeFromLatestChannel";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToLatestChannel =
  (): AppThunk =>
  async (dispatch, _getState, { db, auth, subscriptions }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "chatLatest");

    try {
      const { id, customClaims } = await getLoggedUser(auth);
      const channelName = `chat-latest-${id}`;

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "chatLatest", claimedSlot)) {
        return;
      }

      const subscription = await openRealtimeChannel(db, channelName, subscriptions.latestChatRealtimeChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "chatLatest", claimedSlot)) {
        await db.removeChannel(subscription);
        return;
      }

      subscriptions.latestChatRealtimeChannel = subscription;
      subscriptions.latestChatChannelName = channelName;

      subscription
        .on<ChatChannel>(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "chat_channel",
            // Unfiltered, every online client receives every chat_channel event on the
            // platform (RLS evaluated per subscriber) — a bulk broadcast to N clients
            // becomes M×N deliveries. Realtime filters cannot express array containment,
            // so clients filter on the stamped clientUserId column; staff keeps the wide
            // subscription — they legitimately watch many channels (TRE-1951).
            ...(customClaims?.role === UserRole.CLIENT ? { filter: `clientUserId=eq.${id}` } : {}),
          },
          async (payload) => {
            if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
              const { data } = await db
                .from("chat_channel")
                .select(
                  "*, lastMessage:lastMessageId(id, content, createdAt, files), profiles:user_profile(avatarUrl, firstName, lastName, id, email))",
                )
                .eq("id", payload.new.id)
                .single<ChatChannelWithLastMessage>();

              dispatch(updateChannel(data));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToLatestChannelSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromLatestChannel());
          }
        });
    } catch (error) {
      dispatch(subscribeToLatestChannelFailed());
    }
  };
